export interface RateLimiterOptions {
    maxRequests: number;
    windowMs?: number;
    retryBufferMs?: number;
    /** Factory for abort errors when `signal` is aborted. Defaults to a generic AbortError. */
    createAbortError?: () => Error;
}

const DEFAULT_WINDOW_MS = 60_000;
const DEFAULT_RETRY_BUFFER_MS = 5_000;

const defaultCreateAbortError = (): DOMException => new DOMException('Aborted', 'AbortError');

/**
 * Sliding-window request rate limiter with optional AbortSignal support.
 * Layer-agnostic — callers supply abort error semantics if needed (e.g. uploads).
 */
export class RateLimiter {
    private readonly maxRequests: number;
    private readonly windowMs: number;
    private readonly retryBufferMs: number;
    private readonly createAbortError: () => Error;
    private readonly timestamps: number[] = [];
    private tail: Promise<void> = Promise.resolve();
    private pausedUntil = 0;

    constructor(options: RateLimiterOptions) {
        this.maxRequests = options.maxRequests;
        this.windowMs = options.windowMs ?? DEFAULT_WINDOW_MS;
        this.retryBufferMs = options.retryBufferMs ?? DEFAULT_RETRY_BUFFER_MS;
        this.createAbortError = options.createAbortError ?? defaultCreateAbortError;
    }

    public get activeCount(): number {
        this.prune();
        return this.timestamps.length;
    }

    public isGloballyPaused(): boolean {
        return Date.now() < this.pausedUntil;
    }

    public pauseUntil(timestamp: number): void {
        this.pausedUntil = Math.max(this.pausedUntil, timestamp);
    }

    public clearPause(): void {
        this.pausedUntil = 0;
    }

    public async waitUntilReady(signal?: AbortSignal, onWaiting?: (resumeAt: number) => void): Promise<void> {
        while (Date.now() < this.pausedUntil) {
            this.throwIfAborted(signal);
            const resumeAt = this.pausedUntil;
            onWaiting?.(resumeAt);
            await this.delay(resumeAt - Date.now(), signal);
        }
    }

    public async acquire(signal?: AbortSignal, onWaiting?: (resumeAt: number) => void): Promise<void> {
        return new Promise((resolve, reject) => {
            const run = async (): Promise<void> => {
                try {
                    await this.waitForSlot(signal, onWaiting);
                    resolve();
                } catch (error) {
                    reject(error);
                }
            };

            this.tail = this.tail.then(run, run);
        });
    }

    private throwIfAborted(signal?: AbortSignal): void {
        if (signal?.aborted) {
            throw this.createAbortError();
        }
    }

    private delay(ms: number, signal?: AbortSignal): Promise<void> {
        return new Promise((resolve, reject) => {
            if (ms <= 0) {
                resolve();
                return;
            }

            if (signal?.aborted) {
                reject(this.createAbortError());
                return;
            }

            const timeout = setTimeout(() => {
                cleanup();
                resolve();
            }, ms);

            const onAbort = (): void => {
                cleanup();
                reject(this.createAbortError());
            };

            const cleanup = (): void => {
                clearTimeout(timeout);
                signal?.removeEventListener('abort', onAbort);
            };

            signal?.addEventListener('abort', onAbort, { once: true });
        });
    }

    private async waitForSlot(signal?: AbortSignal, onWaiting?: (resumeAt: number) => void): Promise<void> {
        while (true) {
            this.throwIfAborted(signal);
            await this.waitUntilReady(signal, onWaiting);
            this.prune();

            if (this.timestamps.length < this.maxRequests) {
                this.timestamps.push(Date.now());
                return;
            }

            const oldestTimestamp = this.timestamps[0];
            if (oldestTimestamp === undefined) {
                continue;
            }

            const waitMs = Math.max(
                this.retryBufferMs,
                oldestTimestamp + this.windowMs - Date.now() + this.retryBufferMs,
            );

            onWaiting?.(Date.now() + waitMs);
            await this.delay(waitMs, signal);
        }
    }

    private prune(now = Date.now()): void {
        const cutoff = now - this.windowMs;

        while (this.timestamps.length > 0) {
            const oldest = this.timestamps[0];
            if (oldest === undefined || oldest > cutoff) {
                break;
            }

            this.timestamps.shift();
        }
    }
}
